// The HQ console's paths (spec/14), at the root of its own origin (`https://hq.{domain}/…`).
//
// Plain strings on purpose: TanStack Router checks links against one registered route tree, and the
// registered one is the organisation console's (`src/router.tsx`). So HQ's links name their paths
// from here, and `src/__tests__/hq.test.tsx` opens every one of them.

export type HqPath =
  | 'home'
  | 'live'
  | 'leads'
  | 'pipeline'
  | 'funnel'
  | 'lead'
  | 'organizations'
  | 'newOrganization'
  | 'organization'
  | 'venues'
  | 'catalogue'
  | 'subscriptions'
  | 'players'
  | 'player'
  | 'fleet'
  | 'settings'
  | 'settingsGroup'
  | 'brand'
  | 'providers'
  | 'organizationSettings'
  | 'organizationSettingsGroup'
  | 'organizationMigration'
  | 'organizationMigrationRun'
  | 'audit';

export const HQ_PATHS: Readonly<Record<HqPath, string>> = {
  home: '/',
  /** HQ Live: the Platform right now — sessions, stations, venues online, takings today (spec/14 §2, F-ANL-02). */
  live: '/live',
  /** The sales pipeline (spec/14 §16, F-HQ-39): Today is its front page, then the Pipeline, the Funnel, and one lead. */
  leads: '/leads',
  pipeline: '/leads/pipeline',
  funnel: '/leads/funnel',
  lead: '/leads/$leadId',
  /** Every organisation on the platform, and creating one (spec/14 §3.1). */
  organizations: '/organizations',
  newOrganization: '/organizations/new',
  /** One organisation's record, its owner's invitation, and the way to its settings. */
  organization: '/organizations/$organizationId',
  /** Every organisation's venues, and their listing flags (spec/14 §3.3). */
  venues: '/venues',
  /** The platform game catalogue: search the metadata provider and add games (spec/14 §6). */
  catalogue: '/catalogue',
  /** Subscriptions → Add-ons: the catalogue of add-ons and the price of each, per PC a month (spec/14 §4.5, F-BOOT-17). */
  subscriptions: '/subscriptions',
  /** Looking a person up across the Platform, and one player's platform record (spec/14 §10). */
  players: '/players',
  player: '/players/$playerId',
  /** Fleet: station client releases, each channel's staged rollout, and the fleet's versions (spec/14 §12, F-STN-12). */
  fleet: '/fleet',
  /** Platform defaults: the registry at platform scope, and its locks (spec/14 §14). */
  settings: '/settings',
  settingsGroup: '/settings/$group',
  /** The brand configuration's runtime values (spec/14 §14, spec/03 §12.1). */
  brand: '/brand',
  /** The external providers the deployment is wired to, and a safe check of each (spec/14 §15, F-HQ-38). */
  providers: '/providers',
  /** One organisation's settings, to set or lock one there (spec/03 §9). */
  organizationSettings: '/organizations/$organizationId/settings',
  organizationSettingsGroup: '/organizations/$organizationId/settings/$group',
  /** Moving the organisation's players from another system: its moves, and one move's steps (spec/14 §3.5, F-HQ-40). */
  organizationMigration: '/organizations/$organizationId/migration',
  organizationMigrationRun: '/organizations/$organizationId/migration/$migrationId',
  /** Every organisation's audit log and the platform's own, read with a reason (spec/14 §15, spec/07 §11.2). */
  audit: '/audit',
};
